import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  ApiError,
  type LandingBlock,
  type LandingConfig,
  type LandingPage,
  type LandingPageInput,
  type LandingPagesResponse,
  type LandingStatus,
  type LocalizedText,
} from '../types/api';

const STATUS_LABEL: Record<LandingStatus, string> = { draft: 'Draft', live: 'Live', off: 'Off' };
const MAX_BLOCKS = 30;
const HEX = /^#[0-9a-fA-F]{6}$/;
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const LANGS = [
  { value: 'default', label: 'Default' },
  { value: 'da', label: 'Danish' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
] as const;
type LangKey = (typeof LANGS)[number]['value'];

const BLOCK_LABEL: Record<LandingBlock['type'], string> = {
  heading: 'Heading',
  text: 'Text',
  image: 'Image',
  form: 'Signup box',
  button: 'Button',
  coupon: 'Coupon',
};

const NEW_PAGE: LandingPageInput = {
  slug: '',
  title: '',
  indexable: false,
  countViews: false,
  formId: null,
  config: {
    blocks: [],
    design: {
      backgroundColor: '#ffffff',
      textColor: '#243a53',
      buttonColor: '#208233',
      align: 'center',
      width: 'medium',
      font: 'sans',
    },
    legal: { imprintUrl: null, privacyUrl: null },
    listId: null,
  },
};

const selectClass = 'h-[42px] w-full rounded-[9px] border border-zinc-300 bg-card px-3 text-sm';

function newBlock(type: LandingBlock['type'], existing: LandingBlock[]): LandingBlock {
  let n = existing.length + 1;
  while (existing.some((b) => b.id === `b${n}`)) n += 1;
  const id = `b${n}`;
  switch (type) {
    case 'heading':
      return { type, id, text: { default: '' }, level: 2 };
    case 'text':
      return { type, id, text: { default: '' } };
    case 'image':
      return { type, id, url: '', alt: { default: '' } };
    case 'form':
      return {
        type,
        id,
        buttonText: { default: 'Sign up' },
        successText: { default: 'Thank you! Check your inbox.' },
      };
    case 'button':
      return { type, id, label: { default: '' }, url: '' };
    case 'coupon':
      return { type, id, code: '', label: { default: 'Your code:' } };
  }
}

function validate(input: LandingPageInput): string | null {
  if (input.title.trim() === '') return 'Enter a title.';
  if (!SLUG.test(input.slug) || input.slug.length > 60) {
    return 'The address can have lower-case letters, digits and single hyphens, up to 60 characters.';
  }
  const d = input.config.design;
  if (![d.backgroundColor, d.textColor, d.buttonColor].every((h) => HEX.test(h))) {
    return 'Colours must look like #208233.';
  }
  const https = (u: string | null) => u === null || u === '' || /^https:\/\/[^\s/]+/.test(u);
  if (!https(input.config.legal.imprintUrl) || !https(input.config.legal.privacyUrl)) {
    return 'The legal links must start with https://.';
  }
  for (const [i, b] of input.config.blocks.entries()) {
    const where = `Block ${i + 1} (${BLOCK_LABEL[b.type]})`;
    if ((b.type === 'heading' || b.type === 'text') && b.text.default.trim() === '') {
      return `${where} needs a default text.`;
    }
    if (
      b.type === 'button' &&
      (b.label.default.trim() === '' || !/^https:\/\/[^\s/]+/.test(b.url))
    ) {
      return `${where} needs a text and a link starting with https://.`;
    }
    if (b.type === 'image' && !/^https:\/\/[^\s/]+/.test(b.url)) {
      return `${where} needs an image address from the media library.`;
    }
    if (
      b.type === 'form' &&
      (b.buttonText.default.trim() === '' || b.successText.default.trim() === '')
    ) {
      return `${where} needs a button text and a thank-you text.`;
    }
    if (b.type === 'coupon' && !/^[A-Za-z0-9_-]{1,40}$/.test(b.code)) {
      return `${where} needs a code with letters, digits, - or _.`;
    }
  }
  const forms = input.config.blocks.filter((b) => b.type === 'form').length;
  const coupons = input.config.blocks.filter((b) => b.type === 'coupon').length;
  if (forms > 1) return 'A page can have one signup box.';
  if (coupons > 1) return 'A page can have one coupon block.';
  if (coupons === 1 && forms === 0) return 'A coupon block needs the signup box on the same page.';
  return null;
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

function Check({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

function getText(t: LocalizedText, lang: LangKey): string {
  return lang === 'default' ? t.default : (t[lang] ?? '');
}
function setText(t: LocalizedText, lang: LangKey, value: string): LocalizedText {
  if (lang === 'default') return { ...t, default: value };
  const next = { ...t };
  if (value === '') delete next[lang];
  else next[lang] = value;
  return next;
}

function BlockEditor({
  index,
  count,
  block,
  lang,
  disabled,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  block: LandingBlock;
  lang: LangKey;
  disabled: boolean;
  onChange: (b: LandingBlock) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const name = `${BLOCK_LABEL[block.type]} ${n}`;
  const fid = (k: string) => `blk-${block.id}-${k}`;
  const text = (
    key: string,
    label: string,
    t: LocalizedText,
    set: (t: LocalizedText) => void,
    multiline = false,
  ) => (
    <Field id={fid(key)} label={`${label} (${LANGS.find((l) => l.value === lang)?.label})`}>
      {multiline ? (
        <textarea
          id={fid(key)}
          rows={4}
          maxLength={1000}
          disabled={disabled}
          className="w-full rounded-[9px] border border-zinc-300 bg-card px-3 py-2 text-sm"
          value={getText(t, lang)}
          onChange={(e) => set(setText(t, lang, e.target.value))}
        />
      ) : (
        <Input
          id={fid(key)}
          disabled={disabled}
          value={getText(t, lang)}
          onChange={(e) => set(setText(t, lang, e.target.value))}
        />
      )}
    </Field>
  );
  return (
    <li>
      <fieldset disabled={disabled} className="space-y-3 rounded-lg border border-border p-3">
        <legend className="px-1 text-sm font-semibold">{`Block ${n}: ${BLOCK_LABEL[block.type]}`}</legend>
        {block.type === 'heading' ? (
          <>
            {text('text', 'Heading', block.text, (t) => onChange({ ...block, text: t }))}
            <Field id={fid('level')} label="Size">
              <select
                id={fid('level')}
                className={selectClass}
                value={block.level}
                onChange={(e) => onChange({ ...block, level: e.target.value === '1' ? 1 : 2 })}
              >
                <option value="1">Large</option>
                <option value="2">Medium</option>
              </select>
            </Field>
          </>
        ) : null}
        {block.type === 'text'
          ? text('text', 'Text', block.text, (t) => onChange({ ...block, text: t }), true)
          : null}
        {block.type === 'image' ? (
          <>
            <Field
              id={fid('url')}
              label="Image address"
              hint="Copy it from the media library. Other addresses are refused."
            >
              <Input
                id={fid('url')}
                value={block.url}
                onChange={(e) => onChange({ ...block, url: e.target.value })}
              />
            </Field>
            {text('alt', 'Image description', block.alt, (t) => onChange({ ...block, alt: t }))}
          </>
        ) : null}
        {block.type === 'form' ? (
          <>
            {text('btn', 'Button text', block.buttonText, (t) =>
              onChange({ ...block, buttonText: t }),
            )}
            {text('ok', 'Thank-you text', block.successText, (t) =>
              onChange({ ...block, successText: t }),
            )}
            <p className="text-xs text-muted">
              Asks for an email address only. Needs the shop's Turnstile key to show.
            </p>
          </>
        ) : null}
        {block.type === 'button' ? (
          <>
            {text('label', 'Button text', block.label, (t) => onChange({ ...block, label: t }))}
            <Field id={fid('url')} label="Link" hint="Must start with https://.">
              <Input
                id={fid('url')}
                value={block.url}
                onChange={(e) => onChange({ ...block, url: e.target.value })}
              />
            </Field>
          </>
        ) : null}
        {block.type === 'coupon' ? (
          <>
            <Field
              id={fid('code')}
              label="Code"
              hint="Shown only after a signup on this page went through. It is never in the page source."
            >
              <Input
                id={fid('code')}
                value={block.code}
                onChange={(e) => onChange({ ...block, code: e.target.value })}
              />
            </Field>
            {text('label', 'Line above the code', block.label, (t) =>
              onChange({ ...block, label: t }),
            )}
          </>
        ) : null}
        {!disabled ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={index === 0}
              aria-label={`Move ${name} up`}
              onClick={() => onMove(-1)}
            >
              Move up
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={index === count - 1}
              aria-label={`Move ${name} down`}
              onClick={() => onMove(1)}
            >
              Move down
            </Button>
            <Button size="sm" variant="danger" aria-label={`Remove ${name}`} onClick={onRemove}>
              Remove
            </Button>
          </div>
        ) : null}
      </fieldset>
    </li>
  );
}

function Editor({
  initial,
  page,
  readOnly,
  onClose,
}: {
  initial: LandingPageInput;
  page: LandingPage | null;
  readOnly: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<LandingPageInput>(initial);
  const [lang, setLang] = useState<LangKey>('default');
  const [saved, setSaved] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [addType, setAddType] = useState<LandingBlock['type']>('heading');

  const save = useMutation({
    mutationFn: (input: LandingPageInput) =>
      apiFetch<LandingPage>(page ? `/landing-pages/${page.id}` : '/landing-pages', {
        method: page ? 'PUT' : 'POST',
        body: input,
      }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
      if (!page) onClose();
    },
  });
  const preview = useMutation({
    mutationFn: () =>
      apiFetch<{ html: string }>(`/landing-pages/${page!.id}/preview`, {
        method: 'POST',
        body: {
          config: draft.config,
          title: draft.title.trim() || page!.title,
          lang: lang === 'default' ? undefined : lang,
        },
      }),
    onSuccess: (r) => setPreviewHtml(r.html),
  });

  const problem = validate(draft);
  const c = draft.config;
  const errorOf = (e: unknown) =>
    e instanceof ApiError
      ? (e.body.message ?? 'That did not work. Try again.')
      : 'That did not work. Try again.';

  function edit(next: Partial<LandingPageInput>) {
    save.reset();
    setSaved(false);
    setDraft((d) => ({ ...d, ...next }));
  }
  function config(next: Partial<LandingConfig>) {
    edit({ config: { ...c, ...next } });
  }
  function setBlocks(blocks: LandingBlock[]) {
    config({ blocks });
  }
  const slugLocked = page?.status === 'live';

  return (
    <Card className="space-y-5 p-4">
      <h2 className="text-lg font-semibold">{page ? `Edit ${page.title}` : 'New landing page'}</h2>
      <fieldset disabled={readOnly} className="grid gap-3 sm:grid-cols-2">
        <legend className="sr-only">Page</legend>
        <Field id="lp-title" label="Title">
          <Input
            id="lp-title"
            value={draft.title}
            maxLength={120}
            onChange={(e) => edit({ title: e.target.value })}
          />
        </Field>
        <Field
          id="lp-slug"
          label="Address"
          hint={
            slugLocked
              ? 'A live page keeps its address. Turn it off to change it.'
              : 'The page is at /p/ and this address.'
          }
        >
          <Input
            id="lp-slug"
            value={draft.slug}
            disabled={slugLocked}
            onChange={(e) => edit({ slug: e.target.value })}
          />
        </Field>
      </fieldset>

      <fieldset disabled={readOnly} className="space-y-1">
        <legend className="text-sm font-semibold">Search and numbers</legend>
        <Check
          id="lp-index"
          label="Let search engines list this page"
          checked={draft.indexable}
          onChange={(v) => edit({ indexable: v })}
        />
        <Check
          id="lp-views"
          label="Count views"
          checked={draft.countViews}
          onChange={(v) => edit({ countViews: v })}
        />
        <p className="text-xs text-muted">
          Both are off by default. The page sets no cookies and stores nothing about the visitor.
          Views are one anonymous count per day; signups are always counted.
        </p>
      </fieldset>

      <fieldset disabled={readOnly} className="space-y-3">
        <legend className="text-sm font-semibold">Look</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {(['backgroundColor', 'textColor', 'buttonColor'] as const).map((k) => (
            <Field
              key={k}
              id={`lp-${k}`}
              label={
                k === 'backgroundColor'
                  ? 'Background colour'
                  : k === 'textColor'
                    ? 'Text colour'
                    : 'Button colour'
              }
            >
              <Input
                id={`lp-${k}`}
                value={c.design[k]}
                onChange={(e) => config({ design: { ...c.design, [k]: e.target.value } })}
              />
            </Field>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field id="lp-align" label="Alignment">
            <select
              id="lp-align"
              className={selectClass}
              value={c.design.align}
              onChange={(e) =>
                config({ design: { ...c.design, align: e.target.value as 'left' | 'center' } })
              }
            >
              <option value="center">Centred</option>
              <option value="left">Left</option>
            </select>
          </Field>
          <Field id="lp-width" label="Width">
            <select
              id="lp-width"
              className={selectClass}
              value={c.design.width}
              onChange={(e) =>
                config({
                  design: { ...c.design, width: e.target.value as 'narrow' | 'medium' | 'wide' },
                })
              }
            >
              <option value="narrow">Narrow</option>
              <option value="medium">Medium</option>
              <option value="wide">Wide</option>
            </select>
          </Field>
          <Field id="lp-font" label="Font">
            <select
              id="lp-font"
              className={selectClass}
              value={c.design.font}
              onChange={(e) =>
                config({ design: { ...c.design, font: e.target.value as 'sans' | 'serif' } })
              }
            >
              <option value="sans">Sans-serif</option>
              <option value="serif">Serif</option>
            </select>
          </Field>
        </div>
      </fieldset>

      <fieldset disabled={readOnly} className="space-y-3">
        <legend className="text-sm font-semibold">Legal links</legend>
        <p className="text-xs text-muted">
          Both are needed before the page can go live. Link to the company's own pages; nothing is
          written for you.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="lp-imprint"
            label="Imprint link"
            hint="Empty uses the legal notice address from the brand kit."
          >
            <Input
              id="lp-imprint"
              value={c.legal.imprintUrl ?? ''}
              onChange={(e) =>
                config({
                  legal: { ...c.legal, imprintUrl: e.target.value === '' ? null : e.target.value },
                })
              }
            />
          </Field>
          <Field id="lp-privacy" label="Privacy link">
            <Input
              id="lp-privacy"
              value={c.legal.privacyUrl ?? ''}
              onChange={(e) =>
                config({
                  legal: { ...c.legal, privacyUrl: e.target.value === '' ? null : e.target.value },
                })
              }
            />
          </Field>
        </div>
      </fieldset>

      <section className="space-y-3" aria-labelledby="lp-blocks-h">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h3 id="lp-blocks-h" className="text-sm font-semibold">
            Blocks ({c.blocks.length} of {MAX_BLOCKS})
          </h3>
          <div className="w-48">
            <Field
              id="lp-lang"
              label="Language being edited"
              hint="Empty languages use the default text."
            >
              <select
                id="lp-lang"
                className={selectClass}
                value={lang}
                onChange={(e) => setLang(e.target.value as LangKey)}
              >
                {LANGS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>
        {c.blocks.length === 0 ? <p className="text-sm text-muted">No blocks yet.</p> : null}
        <ol className="space-y-3">
          {c.blocks.map((b, i) => (
            <BlockEditor
              key={b.id}
              index={i}
              count={c.blocks.length}
              block={b}
              lang={lang}
              disabled={readOnly}
              onChange={(nb) => setBlocks(c.blocks.map((x, j) => (j === i ? nb : x)))}
              onMove={(delta) => {
                const next = [...c.blocks];
                const [moved] = next.splice(i, 1);
                next.splice(i + delta, 0, moved!);
                setBlocks(next);
              }}
              onRemove={() => setBlocks(c.blocks.filter((_, j) => j !== i))}
            />
          ))}
        </ol>
        {!readOnly ? (
          <div className="flex flex-wrap items-end gap-2">
            <Field id="lp-add-type" label="New block">
              <select
                id="lp-add-type"
                className={selectClass}
                value={addType}
                onChange={(e) => setAddType(e.target.value as LandingBlock['type'])}
              >
                {(Object.keys(BLOCK_LABEL) as LandingBlock['type'][]).map((t) => (
                  <option key={t} value={t}>
                    {BLOCK_LABEL[t]}
                  </option>
                ))}
              </select>
            </Field>
            <Button
              variant="secondary"
              disabled={c.blocks.length >= MAX_BLOCKS}
              onClick={() => setBlocks([...c.blocks, newBlock(addType, c.blocks)])}
            >
              Add block
            </Button>
          </div>
        ) : null}
      </section>

      {problem ? <p className="text-sm text-danger-text">{problem}</p> : null}
      {save.isError ? <Alert>{errorOf(save.error)}</Alert> : null}
      {preview.isError ? <Alert>{errorOf(preview.error)}</Alert> : null}
      {saved ? (
        <div role="status" className="text-success-text text-sm">
          Saved.
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {!readOnly ? (
          <Button disabled={problem !== null || save.isPending} onClick={() => save.mutate(draft)}>
            Save
          </Button>
        ) : null}
        {page && !readOnly ? (
          <Button
            variant="secondary"
            disabled={problem !== null || preview.isPending}
            onClick={() => preview.mutate()}
          >
            Preview
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      {previewHtml !== null ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Preview</h3>
          <iframe
            title="Landing page preview"
            sandbox=""
            srcDoc={previewHtml}
            className="h-[640px] w-full max-w-[390px] rounded-lg border border-border bg-white"
          />
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Hosted landing pages (K5 part 2): a list with status and numbers, and an
 * editor made of blocks. A new page is a draft and is not public until an
 * editor sets it live; at most 20 can be live.
 */
export function LandingPagesPage() {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<'new' | string | null>(null);

  const data = useQuery({
    queryKey: ['landing-pages'],
    queryFn: () => apiFetch<LandingPagesResponse>('/landing-pages'),
  });
  const act = useMutation({
    mutationFn: (req: { path: string; method: string; body?: unknown }) =>
      apiFetch<unknown>(req.path, { method: req.method, body: req.body }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['landing-pages'] }),
  });
  const actError =
    act.error instanceof ApiError
      ? (act.error.body.message ?? 'That did not work.')
      : act.isError
        ? 'That did not work.'
        : null;

  if (data.isPending) return <p className="p-6 text-sm text-muted">Loading landing pages...</p>;
  if (data.isError) return <Alert className="m-6">Could not load the landing pages.</Alert>;

  const { pages, stats } = data.data;
  const current =
    editing && editing !== 'new' ? (pages.find((p) => p.id === editing) ?? null) : null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Landing pages</h1>
        {isEditor ? <Button onClick={() => setEditing('new')}>New landing page</Button> : null}
      </div>
      <p className="text-sm text-muted">
        Pages hosted on the news domain at /p/address. A new page is a draft; nothing is public
        until you set it live. Numbers are for the last {data.data.days} days.
      </p>
      {actError ? <Alert>{actError}</Alert> : null}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Landing pages</caption>
          <thead>
            <tr className="text-left text-muted">
              <th scope="col" className="p-3">
                Title
              </th>
              <th scope="col" className="p-3">
                Address
              </th>
              <th scope="col" className="p-3">
                Status
              </th>
              <th scope="col" className="p-3">
                Views
              </th>
              <th scope="col" className="p-3">
                Signups
              </th>
              <th scope="col" className="p-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => {
              const s = stats.find((x) => x.pageId === p.id);
              return (
                <tr key={p.id} className="border-t border-border">
                  <td className="p-3 font-medium">{p.title}</td>
                  <td className="p-3">
                    {p.status === 'live' && p.url ? (
                      <a
                        className="underline"
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        /p/{p.slug}
                      </a>
                    ) : (
                      <span>/p/{p.slug}</span>
                    )}
                  </td>
                  <td className="p-3">{STATUS_LABEL[p.status]}</td>
                  <td className="p-3">{p.countViews ? (s?.views ?? 0) : '-'}</td>
                  <td className="p-3">{s?.signups ?? 0}</td>
                  <td className="space-x-2 p-3 whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="secondary"
                      aria-label={`${isEditor ? 'Edit' : 'View'} ${p.title}`}
                      onClick={() => setEditing(p.id)}
                    >
                      {isEditor ? 'Edit' : 'View'}
                    </Button>
                    {isEditor ? (
                      <>
                        {p.status === 'live' ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label={`Unpublish ${p.title}`}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Unpublish ${p.title}? The address will show "not found" within about a minute.`,
                                )
                              ) {
                                act.mutate({
                                  path: `/landing-pages/${p.id}/status`,
                                  method: 'POST',
                                  body: { status: 'off' },
                                });
                              }
                            }}
                          >
                            Unpublish
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            aria-label={`Publish ${p.title}`}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Publish ${p.title}? Anyone with the link can then see it.`,
                                )
                              ) {
                                act.mutate({
                                  path: `/landing-pages/${p.id}/status`,
                                  method: 'POST',
                                  body: { status: 'live' },
                                });
                              }
                            }}
                          >
                            Publish
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="danger"
                          aria-label={`Delete ${p.title}`}
                          onClick={() => {
                            if (window.confirm(`Delete ${p.title}? Its numbers are deleted too.`)) {
                              act.mutate({ path: `/landing-pages/${p.id}`, method: 'DELETE' });
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {pages.length === 0 ? (
          <p className="p-3 text-sm text-muted">No landing pages yet.</p>
        ) : null}
      </Card>

      {editing ? (
        <Editor
          key={editing}
          initial={
            current
              ? {
                  slug: current.slug,
                  title: current.title,
                  indexable: current.indexable,
                  countViews: current.countViews,
                  formId: current.formId,
                  config: current.config,
                }
              : NEW_PAGE
          }
          page={current}
          readOnly={!isEditor}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
